import {Constants} from 'utils/constants';

import Provider from '../provider';

import {SearchTimeMeasurer, sendToStatist} from '@time-webkit/statist';

import AtMentionSuggestion from './at_mention_suggestion';

const regexForAtMention = /(?:^|\W)@([\p{L}\d\-_. ]*)$/iu;

// The AtMentionProvider provides matches for at mentions, including @here, @channel, @all,
// users in the channel and users not in the channel. It mixes together results from the local
// store with results fetched from the server.
export default class AtMentionProvider extends Provider {
    searchTimeMeasurer = new SearchTimeMeasurer('editor.searchUser.result')
    hasBeenTriggered = false
    constructor(props) {
        super();

        this.setProps(props);

        this.data = null;
        this.lastCompletedWord = '';
        this.lastPrefixWithNoResults = '';
        this.triggerCharacter = '@';
    }

    // setProps gives the provider additional context for matching pretexts. Ideally this would
    // just be something akin to a connected component with access to the store itself.
    setProps({
        currentUserId, channelId, autocompleteUsersInChannel, useChannelMentions, useGroupMentions,
        searchAssociatedGroupsForReference, withinThread,
    }) {
        this.currentUserId = currentUserId;
        this.channelId = channelId;
        this.autocompleteUsersInChannel = autocompleteUsersInChannel;
        this.useChannelMentions = useChannelMentions;
        this.useGroupMentions = useGroupMentions;
        this.searchAssociatedGroupsForReference = searchAssociatedGroupsForReference;
        this.withinThread = withinThread;
    }

    // specialMentions matches one of @here, @channel or @all, unless using /msg.
    specialMentions() {
        if (this.latestPrefix.startsWith('/msg') || !this.useChannelMentions) {
            return [];
        }

        return ['here', 'channel', 'all'].filter((item) =>
            item.includes(this.latestPrefix),
        ).map((name) => ({
            username: name,
            type: Constants.MENTION_SPECIAL,
        }));
    }

    // remoteMembers matches the users listed in the channel by the server.
    remoteMembers() {
        return (this.data?.users ?? []).
            map((profile) => this.createFromProfile(profile, Constants.MENTION_MEMBERS));
    }

    // remoteNonMembers matches users listed as not in the channel by the server.
    remoteNonMembers() {
        return (this.data?.out_of_channel ?? []).
            map((profile) => this.createFromProfile(profile, Constants.MENTION_NONMEMBERS));
    }

    // remoteGroups matches the users listed in the channel by the server.
    remoteGroups() {
        return (this.data?.groups ?? []).
            map((group) => this.createFromGroup(group, Constants.MENTION_GROUPS));
    }

    items() {
        return this.specialMentions().concat(this.remoteMembers(), this.remoteNonMembers(), this.remoteGroups());
    }

    // updateMatches invokes the resultCallback with the metadata for rendering at mentions
    updateMatches(resultCallback, items, prefix) {
        if (items.length === 0) {
            this.lastPrefixWithNoResults = this.latestPrefix;
        } else if (this.lastPrefixWithNoResults === this.latestPrefix) {
            this.lastPrefixWithNoResults = '';
        }
        const mentions = items.map((item) => {
            if (item.username) {
                return '@' + item.username;
            } else if (item.name) {
                return '@' + item.name;
            }
            return '';
        });

        this.searchTimeMeasurer.measure(prefix, mentions.length, {}, false);

        resultCallback({
            matchedPretext: `@${this.latestPrefix}`,
            terms: mentions,
            items,
            component: AtMentionSuggestion,
        });
    }

    handlePretextChanged(pretext, resultCallback) {
        const captured = regexForAtMention.exec(pretext.toLowerCase());
        if (!captured) {
            this.hasBeenTriggered = false;
            return false;
        }

        if (this.lastCompletedWord && captured[0].trim().startsWith(this.lastCompletedWord.trim())) {
            // It appears we're still matching a channel handle that we already completed
            this.hasBeenTriggered = false;
            return false;
        }

        const prefix = captured[1];
        if (this.lastPrefixWithNoResults && prefix.startsWith(this.lastPrefixWithNoResults)) {
            // Just give up since we know it won't return any results
            this.hasBeenTriggered = false;
            return false;
        }

        if (!this.hasBeenTriggered) {
            sendToStatist('editor.searchUser.tap', {
                sourceSearch: 'type',
                sourceTap: this.withinThread ? 'thread' : 'post',
            });
            this.hasBeenTriggered = true;
        }

        this.startNewRequest(prefix);
        this.searchTimeMeasurer.measure(prefix);
        this.updateMatches(resultCallback, this.items(), prefix);

        // If we haven't gotten server-side results in 500 ms, add the loading indicator.
        let showLoadingIndicator = setTimeout(() => {
            if (this.shouldCancelDispatch(prefix)) {
                return;
            }

            this.updateMatches(resultCallback, this.items().concat([{
                type: Constants.MENTION_MORE_MEMBERS,
                loading: true,
            }]));

            showLoadingIndicator = null;
        }, 500);

        // Query the server for remote results to add to the local results.
        this.autocompleteUsersInChannel(prefix).then(({data}) => {
            if (showLoadingIndicator) {
                clearTimeout(showLoadingIndicator);
            }
            if (this.shouldCancelDispatch(prefix)) {
                return;
            }
            this.data = data;

            if (!this.useGroupMentions) {
                this.updateMatches(resultCallback, this.items());
                return;
            }

            this.searchAssociatedGroupsForReference(prefix).then((groupsData) => {
                if (this.data && groupsData && groupsData.data) {
                    this.data.groups = groupsData.data;
                }
                this.updateMatches(resultCallback, this.items());
            });
        });

        return true;
    }

    handleCompleteWord(term) {
        this.lastCompletedWord = term;
        this.lastPrefixWithNoResults = '';
        this.searchTimeMeasurer.dispose();
        this.hasBeenTriggered = false;
    }

    createFromProfile(profile, type) {
        if (profile.id === this.currentUserId) {
            return {
                type,
                ...profile,
                isCurrentUser: true,
            };
        }

        return {
            type,
            ...profile,
        };
    }

    createFromGroup(group, type) {
        return {
            type,
            ...group,
        };
    }
}
